import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';

import { Box } from '@mui/material';

import type { RecordingState } from '@karehero/models';

import { KeniOrb } from '@/components/atoms/KeniOrb/KeniOrb';

/** Orb diameter and its clearance above the bar. */
export const ORB_SIZE = 80;
export const ORB_CLEARANCE = ORB_SIZE / 2 + 12;

/** Room between the field and the orb, or the card's top edge, in spacing steps. */
const FIELD_TOP_GAP = 1;

/** The bar grows to make room, then the orb scales in with a slight overshoot. */
const BAR_GROW = { duration: 280, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' };
const ORB_ARRIVE = {
  duration: 360,
  easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  delay: 120,
};
/** The orb pulls back and shrinks away, then the bar closes up after it. */
const ORB_LEAVE = {
  duration: 300,
  easing: 'cubic-bezier(0.36, 0, 0.66, -0.56)',
};
const BAR_CLOSE = { ...BAR_GROW, delay: 260 };

const REDUCED_MOTION = '@media (prefers-reduced-motion: reduce)';

/** Whether the orb is on the bar, flipped a layout after it is wanted so its arrival transitions. */
const useIsOrbIn = (
  isWanted: boolean,
  barRef: RefObject<HTMLDivElement | null>,
) => {
  const [isIn, setIsIn] = useState(false);

  useLayoutEffect(() => {
    // Reading layout commits the bar without the orb as the transition's start.
    if (isWanted) void barRef.current?.offsetHeight;
    setIsIn(isWanted);
  }, [isWanted, barRef]);

  return isIn;
};

interface OrbRecorder {
  state: RecordingState;
  onStart: () => void;
  onStop: () => void;
}

const OrbButton = ({
  recorder,
  isIn,
  isSubmitting,
}: {
  recorder: OrbRecorder;
  isIn: boolean;
  isSubmitting?: boolean;
}) => {
  const isRecording = recorder.state === 'recording';

  return (
    <Box
      component='button'
      type='button'
      aria-label={isRecording ? 'Stop recording' : 'Record a voice note'}
      aria-hidden={!isIn}
      disabled={isSubmitting || !isIn}
      onClick={isRecording ? recorder.onStop : recorder.onStart}
      sx={(theme) => ({
        position: 'absolute',
        top: 0,
        left: '50%',
        p: 0,
        border: 0,
        bgcolor: 'transparent',
        transform: `translate(-50%, -50%) scale(${isIn ? 1 : 0})`,
        opacity: isIn ? 1 : 0,
        pointerEvents: isIn ? 'auto' : 'none',
        transition: theme.transitions.create(
          ['transform', 'opacity'],
          isIn ? ORB_ARRIVE : ORB_LEAVE,
        ),
        cursor: isSubmitting ? 'default' : 'pointer',
        [REDUCED_MOTION]: { transition: 'none' },
      })}
    >
      <KeniOrb size={ORB_SIZE} state={isRecording ? 'listening' : 'idle'} />
    </Box>
  );
};

export interface OrbDockProps {
  /** Omit to hide the orb. */
  recorder?: OrbRecorder;
  /** Whether the orb should be on the bar. */
  isWanted: boolean;
  isSubmitting?: boolean;
  children: ReactNode;
}

/** OrbDock is the composer's bar with the voice orb riding its top edge. */
export const OrbDock = ({
  recorder,
  isWanted,
  isSubmitting,
  children,
}: OrbDockProps) => {
  const barRef = useRef<HTMLDivElement>(null);
  const isOrbIn = useIsOrbIn(isWanted, barRef);

  return (
    <Box
      ref={barRef}
      sx={(theme) => ({
        position: 'relative',
        px: 3,
        pb: 2,
        pt: isOrbIn
          ? `calc(${ORB_CLEARANCE}px + ${theme.spacing(FIELD_TOP_GAP)})`
          : theme.spacing(1.5 + FIELD_TOP_GAP),
        transition: theme.transitions.create(
          'padding-top',
          isOrbIn ? BAR_GROW : BAR_CLOSE,
        ),
        [REDUCED_MOTION]: { transition: 'none' },
      })}
    >
      {recorder && (
        <OrbButton
          recorder={recorder}
          isIn={isOrbIn}
          isSubmitting={isSubmitting}
        />
      )}
      {children}
    </Box>
  );
};
